class IhcPhotoGallery {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.getElementById(`IhcPhotoGallery-${sectionId}`);
    this.dialog = document.getElementById(`IhcPhotoGalleryDialog-${sectionId}`);
    if (!this.section || !this.dialog) return;

    this.viewport = this.dialog.querySelector('[data-ihc-gallery-viewport]');
    this.track = this.dialog.querySelector('[data-ihc-gallery-track]');
    this.slides = Array.from(this.dialog.querySelectorAll('[data-ihc-gallery-slide]'));
    this.buttons = Array.from(this.section.querySelectorAll(`[data-ihc-gallery-open="${sectionId}"]`));
    this.loadMoreBtn = this.section.querySelector('[data-ihc-gallery-load-more]');
    this.loadMoreCount = Number(this.section.dataset.loadMoreCount) || 10;
    this.currentIndex = 0;
    this.isAnimating = false;

    this.bindEvents();
  }

  bindEvents() {
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.ihcGalleryIndex);
        if (!Number.isNaN(index)) this.open(index);
      });
    });

    this.dialog.querySelector('[data-ihc-gallery-close]')?.addEventListener('click', () => this.close());
    this.dialog.querySelector('[data-ihc-gallery-prev]')?.addEventListener('click', () => this.go(-1));
    this.dialog.querySelector('[data-ihc-gallery-next]')?.addEventListener('click', () => this.go(1));

    this.loadMoreBtn?.addEventListener('click', () => this.loadMore());

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
    });

    let touchStartX = 0;
    let touchEndX = 0;

    this.viewport?.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.viewport?.addEventListener(
      'touchend',
      (event) => {
        touchEndX = event.changedTouches[0].screenX;
        const delta = touchEndX - touchStartX;
        if (Math.abs(delta) < 50) return;
        if (delta < 0) this.go(1);
        else this.go(-1);
      },
      { passive: true }
    );
  }

  getImage(slide) {
    return slide.querySelector('[data-ihc-gallery-slide-image]');
  }

  loadImage(slide) {
    const img = this.getImage(slide);
    if (!img) return Promise.resolve();

    const src = img.dataset.src;
    if (!src) return Promise.resolve();

    if (img.dataset.loaded === 'true' && img.complete && img.naturalWidth > 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const onDone = () => {
        img.removeEventListener('load', onDone);
        img.removeEventListener('error', onDone);
        img.dataset.loaded = 'true';
        resolve();
      };

      img.addEventListener('load', onDone);
      img.addEventListener('error', onDone);
      img.src = src;
    });
  }

  preloadAround(index) {
    [index - 1, index + 1].forEach((i) => {
      if (i >= 0 && i < this.slides.length) {
        this.loadImage(this.slides[i]);
      }
    });
  }

  setTrackPosition(index, animate) {
    if (!this.track) return;
    this.track.style.transition = animate ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
    this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
  }

  async open(index) {
    this.currentIndex = index;
    this.viewport?.classList.add('is-loading');

    await this.loadImage(this.slides[index]);
    this.setTrackPosition(index, false);
    this.preloadAround(index);

    this.viewport?.classList.remove('is-loading');

    if (!this.dialog.open) {
      this.dialog.showModal();
    }
  }

  async go(direction) {
    if (this.isAnimating) return;

    const nextIndex = this.currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= this.slides.length) return;

    const nextSlide = this.slides[nextIndex];
    const nextImage = this.getImage(nextSlide);
    const needsLoad = !nextImage || nextImage.dataset.loaded !== 'true';

    this.isAnimating = true;

    if (needsLoad) {
      this.viewport?.classList.add('is-loading');
      await this.loadImage(nextSlide);
      this.viewport?.classList.remove('is-loading');
    }

    this.currentIndex = nextIndex;
    this.setTrackPosition(nextIndex, true);
    this.preloadAround(nextIndex);

    window.setTimeout(() => {
      this.isAnimating = false;
    }, 450);
  }

  close() {
    this.dialog.close();
    this.isAnimating = false;
    this.viewport?.classList.remove('is-loading');
  }

  hiddenItems() {
    return Array.from(this.section.querySelectorAll('.ihc-photo-gallery__item--hidden'));
  }

  loadMore() {
    const items = this.hiddenItems().slice(0, this.loadMoreCount);
    items.forEach((item) => item.classList.remove('ihc-photo-gallery__item--hidden'));

    if (this.hiddenItems().length === 0) {
      this.loadMoreBtn?.closest('.ihc-photo-gallery__load-more-wrap')?.remove();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-ihc-photo-gallery]').forEach((section) => {
    const sectionId = section.dataset.ihcPhotoGallery;
    if (sectionId && !section.dataset.ihcGalleryReady) {
      section.dataset.ihcGalleryReady = 'true';
      new IhcPhotoGallery(sectionId);
    }
  });
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-ihc-photo-gallery]');
    if (!section || section.dataset.ihcGalleryReady) return;
    section.dataset.ihcGalleryReady = 'true';
    new IhcPhotoGallery(section.dataset.ihcPhotoGallery);
  });
}
